import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { CellProps } from 'react-table'
import { IconPaths, ModalSize, QUERIES } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  Button,
  CustomTable,
  DataColumn,
  PlainHeader,
  RowActions,
  StackedCell,
  TableColumn,
  TableEmptyState,
  useModal,
} from 'app/components'
import { linhaApoio, rotuloDispositivo } from '../../../dispositivos/core/rotulos'
import { TelekitDispositivoList, TelekitView } from '../../telekits-list/core/_models'
import { getAlocacoesTelekit } from '../../telekits-list/core/_requests'
import { useRecarregaAoFecharModal } from '../../../dispositivos/core/useRecarregaAoFecharModal'
import { AlocarDispositivoModal } from './AlocarDispositivoModal'
import { EditarAlocacaoModal } from './EditarAlocacaoModal'
import { DesalocarDispositivoModal } from './DesalocarDispositivoModal'

/** Alocações ativas da maleta. Na Padrão, só leitura (aparelhos genéricos). */
export const TelekitDispositivosTab = ({ telekit }: { telekit: TelekitView }) => {
  const { t } = useT()
  const { showModal } = useModal()
  const idTelekit = telekit.id as number
  const somenteLeitura = !!telekit.padrao

  const { data, isFetching, isError, refetch } = useQuery(
    [QUERIES.TELEKIT_ALOCACOES, idTelekit, 'ativas'],
    () => getAlocacoesTelekit(idTelekit, 'ativo=true&sort=dtInicio,desc&page=0&size=100'),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  )
  useRecarregaAoFecharModal(refetch)

  const colunas = useMemo<ReadonlyArray<TableColumn<TelekitDispositivoList>>>(() => {
    const nomeAparelho = (a: TelekitDispositivoList) => rotuloDispositivo(t, a.dispositivo)

    const lista: TableColumn<TelekitDispositivoList>[] = [
      {
        Header: () => (
          <PlainHeader>{t('pagina.telekits.dispositivos.col.dispositivo')}</PlainHeader>
        ),
        id: 'dispositivo',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => {
          const d = row.original.dispositivo
          return (
            <StackedCell
              primary={nomeAparelho(row.original)}
              secondary={linhaApoio(d.fabricante, d.modelo, d.patrimonio)}
            />
          )
        },
      },
      {
        Header: () => <PlainHeader>{t('pagina.telekits.dispositivos.col.ae_title')}</PlainHeader>,
        id: 'aeTitle',
        cellWidth: '140px',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell tabular primary={row.original.aeTitle ?? ''} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.telekits.dispositivos.col.modalidade')}</PlainHeader>,
        id: 'modalidade',
        cellWidth: '110px',
        hideOnMobile: true,
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell primary={row.original.modalidade ?? ''} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.telekits.dispositivos.col.desde')}</PlainHeader>,
        id: 'dtInicio',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <DataColumn data={row.original.dtInicio ?? undefined} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.telekits.dispositivos.col.observacao')}</PlainHeader>,
        id: 'observacao',
        hideOnMobile: true,
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell primary={row.original.observacao ?? ''} />
        ),
      },
    ]

    if (!somenteLeitura) {
      lista.push({
        Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
        id: 'actions',
        cellWidth: '120px',
        align: 'end',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <RowActions
            itemName={nomeAparelho(row.original)}
            primaryAction={{
              label: t('pagina.telekits.dispositivos.editar_alocacao'),
              icon: IconPaths.Pen,
              onClick: () =>
                showModal({
                  title: t('pagina.telekits.dispositivos.editar_alocacao'),
                  description: nomeAparelho(row.original),
                  size: ModalSize.Medium,
                  content: <EditarAlocacaoModal idTelekit={idTelekit} alocacao={row.original} />,
                }),
            }}
            items={[
              {
                label: t('pagina.telekits.dispositivos.desalocar'),
                danger: true,
                onClick: () =>
                  showModal({
                    title: t('pagina.telekits.dispositivos.desalocar_titulo'),
                    description: nomeAparelho(row.original),
                    size: ModalSize.Medium,
                    content: (
                      <DesalocarDispositivoModal idTelekit={idTelekit} alocacao={row.original} />
                    ),
                  }),
              },
            ]}
          />
        ),
      })
    }
    return lista
  }, [t, somenteLeitura, idTelekit, showModal])

  const abrirAlocar = () =>
    showModal({
      title: t('pagina.telekits.dispositivos.alocar_titulo'),
      size: ModalSize.Medium,
      content: <AlocarDispositivoModal telekit={telekit} />,
    })

  return (
    <>
      {somenteLeitura ? (
        <div className='rounded bg-gray-100 text-gray-700 p-5 mb-6' role='note'>
          {t('pagina.telekits.dispositivos.nota_padrao')}
        </div>
      ) : (
        <div className='d-flex justify-content-end mb-4'>
          <Button className='btn-primary' icon={IconPaths.Add} onClick={abrirAlocar}>
            {t('pagina.telekits.dispositivos.alocar')}
          </Button>
        </div>
      )}
      <CustomTable<TelekitDispositivoList>
        id='kt_table_telekit_dispositivos'
        ariaLabel={t('pagina.telekits.dispositivos.aba')}
        data={data?.data ?? []}
        columns={colunas}
        isLoading={isFetching}
        isError={isError}
        onRetry={refetch}
        notPaginated
        emptyState={
          <TableEmptyState
            title={t('pagina.telekits.dispositivos.vazio_titulo')}
            description={
              somenteLeitura ? undefined : t('pagina.telekits.dispositivos.vazio_descricao')
            }
          />
        }
      />
    </>
  )
}
